import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useTable, useGlobalFilter, useFilters, usePagination, useSortBy } from 'react-table';
import { useParams, Link, useHistory } from 'react-router-dom';
import axios from '../../../../../services/AxiosInstance';
import NewSupplierModal from './NewSupplierModal';
import { Dropdown, Spinner, Button, Alert, Row, Col, Badge } from 'react-bootstrap';
import { getApiUrl } from '../../../../../utils/ApiUtils';
import { NoData } from '../../../SharedComponents/NoData';
import PageTitle from '../../../../layouts/PageTitle';
import swal from 'sweetalert';

// Lucide React Icons
import {
  Plus,
  Upload,
  Building,
  Phone,
  CreditCard,
  TrendingUp,
  DollarSign,
  FileText,
  MoreHorizontal,
  Edit3,
  Trash2,
  Eye,
  Search,
  RefreshCw,
  Users,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

export const SuppliersTable = () => {
  const { companyId } = useParams();
  const history = useHistory();
  const [suppliersTableData, setSuppliersTableData] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [modalKey, setModalKey] = useState(0);
  const [globalFilter, setGlobalFilter] = useState('');
  const [metrics, setMetrics] = useState({
    totalSuppliers: 0,
    activeSuppliers: 0,
    totalOutstanding: 0,
    totalInvoices: 0,
    averageInvoiceValue: 0
  });

  const SUPPLIERS_COLUMNS = useMemo(() => [
    {
      Header: 'Leverantör',
      accessor: 'name',
      Cell: ({ row }) => (
        <div className="d-flex align-items-center">
          <div
            className="me-3 d-flex align-items-center justify-content-center"
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              background: 'var(--rgba-primary-1)',
              color: 'var(--bs-primary)'
            }}
          >
            <Building size={20} />
          </div>
          <div>
            <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>
              {row.original.name}
            </div>
            {row.original.orgnumber && (
              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                Org.nr: {row.original.orgnumber}
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      Header: 'Kontakt',
      accessor: 'phone',
      Cell: ({ row }) => (
        <div>
          {row.original.phone && (
            <div className="d-flex align-items-center mb-1">
              <Phone size={14} className="me-2" style={{ color: '#6b7280' }} />
              <span style={{ fontSize: '0.875rem' }}>{row.original.phone}</span>
            </div>
          )}
          {row.original.city && (
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              {row.original.city}
            </div>
          )}
        </div>
      )
    },
    {
      Header: 'Fakturor',
      accessor: 'invoice_count',
      Cell: ({ row }) => (
        <div className="text-center">
          <div className="d-flex align-items-center justify-content-center mb-1">
            <FileText size={14} className="me-2" style={{ color: '#6b7280' }} />
            <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>
              {parseInt(row.original.invoice_count) || 0}
            </span>
          </div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
            fakturor
          </div>
        </div>
      )
    },
    {
      Header: 'Utestående',
      accessor: 'outstanding_amount',
      Cell: ({ row }) => {
        const amount = parseFloat(row.original.outstanding_amount) || 0;
        const overdueCount = parseInt(row.original.overdue_count) || 0;
        const hasOutstanding = amount > 0;

        return (
          <div className="text-end">
            <div className={`d-flex align-items-center justify-content-end mb-1 ${hasOutstanding ? 'text-danger' : 'text-success'}`}>
              <DollarSign size={14} className="me-1" />
              <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                {new Intl.NumberFormat('sv-SE', {
                  style: 'currency',
                  currency: 'SEK',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                }).format(amount)}
              </span>
            </div>
            {overdueCount > 0 && (
              <Badge bg="danger" style={{ fontSize: '0.65rem' }}>
                <AlertTriangle size={10} className="me-1" />
                {overdueCount} förfallen{overdueCount > 1 ? 'a' : ''}
              </Badge>
            )}
            {!overdueCount && hasOutstanding && (
              <Badge bg="warning" style={{ fontSize: '0.65rem' }}>
                Utestående
              </Badge>
            )}
          </div>
        );
      }
    },
    {
      Header: 'Betalt totalt',
      accessor: 'total_paid',
      Cell: ({ row }) => {
        const amount = parseFloat(row.original.total_paid) || 0;

        return (
          <div className="text-end">
            <div className="d-flex align-items-center justify-content-end">
              <CheckCircle size={14} className="me-1" style={{ color: 'var(--bs-success)' }} />
              <span style={{ fontWeight: '600', fontSize: '0.875rem', color: 'var(--bs-success)' }}>
                {new Intl.NumberFormat('sv-SE', {
                  style: 'currency',
                  currency: 'SEK',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                }).format(amount)}
              </span>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              historiskt
            </div>
          </div>
        );
      }
    },
    {
      Header: "",
      id: 'actions',
      Cell: ({ row }) => (
        <div className="d-flex justify-content-end" onClick={(e) => e.stopPropagation()}>
          <Dropdown>
            <Dropdown.Toggle
              variant=""
              className="btn-link border-0 p-2"
              style={{
                background: 'transparent',
                boxShadow: 'none',
                color: '#6b7280'
              }}
            >
              <MoreHorizontal size={16} />
            </Dropdown.Toggle>
            <Dropdown.Menu align="end">
              <Dropdown.Item
                onClick={() => history.push(`/${companyId}/suppliers/${row.original.supplier_id}`)}
                className="d-flex align-items-center"
              >
                <Eye size={16} className="me-2" />
                Visa detaljer
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => handleEditSupplier(row.original)}
                className="d-flex align-items-center"
              >
                <Edit3 size={16} className="me-2" />
                Redigera
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      )
    }
  ], [history, companyId]);

  const fetchSuppliers = useCallback(async (isInitialLoad = false) => {
    if (isInitialLoad) setLoading(true);
    else setRefreshing(true);

    try {
      const getSuppliersApiUrl = getApiUrl(`${companyId}/purchasing/suppliers`);
      const response = await axios.get(getSuppliersApiUrl, { withCredentials: true });
      const processedData = response.data.map(supplier => ({
        ...supplier,
        // Backend now returns these field names
        invoice_count: parseInt(supplier.invoice_count, 10) || 0,
        outstanding_amount: parseFloat(supplier.outstanding_amount) || 0,
        total_paid: parseFloat(supplier.total_paid) || 0,
        overdue_count: parseInt(supplier.overdue_count, 10) || 0,
        total_invoice_amount: parseFloat(supplier.total_invoice_amount) || 0
      }));

      // Calculate metrics
      const calculatedMetrics = {
        totalSuppliers: processedData.length,
        activeSuppliers: processedData.filter(s => s.invoice_count > 0).length,
        totalOutstanding: processedData.reduce((sum, s) => sum + (s.outstanding_amount || 0), 0),
        totalInvoices: processedData.reduce((sum, s) => sum + (s.invoice_count || 0), 0),
        averageInvoiceValue: processedData.length > 0
          ? processedData.reduce((sum, s) => sum + (s.total_invoice_amount || 0), 0) /
          Math.max(1, processedData.reduce((sum, s) => sum + (s.invoice_count || 0), 0))
          : 0
      };

      setSuppliersTableData(processedData || []);
      setMetrics(calculatedMetrics);
      setError(null);
    } catch (error) {
      setError(error);
      swal('Error', 'Failed to fetch suppliers', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchSuppliers(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    gotoPage,
    pageCount,
    setPageSize: setTablePageSize,
    setGlobalFilter: setTableGlobalFilter,
  } = useTable(
    {
      columns: SUPPLIERS_COLUMNS,
      data: suppliersTableData,
      initialState: {
        pageSize,
        sortBy: [{ id: 'name', desc: false }]
      }
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { pageIndex } = state;

  // Function to open modal for creating a new supplier
  const handleNewSupplier = () => {
    setSelectedSupplier(null);
    setModalKey(prevKey => prevKey + 1); // Increment key to force re-render
    setShowModal(true);
  };

  // Function to open modal for editing an existing supplier
  const handleEditSupplier = useCallback((supplier) => {
    setSelectedSupplier(supplier);
    setModalKey(prevKey => prevKey + 1); // Increment key to force re-render
    setShowModal(true);
  }, []);

  // Function to close the modal and reset state
  const handleCloseModal = () => {
    setShowModal(false);
    // We don't reset selectedSupplier here to allow the modal to properly clean up first
  };

  // Sync global filter
  useEffect(() => {
    setTableGlobalFilter(globalFilter);
  }, [globalFilter, setTableGlobalFilter]);

  const handleDeleteSupplier = useCallback(async (supplier) => {
    try {
      // Confirm before deleting
      const willDelete = await swal({
        title: "Är du säker?",
        text: `Vill du ta bort leverantören ${supplier.name}?`,
        icon: "warning",
        buttons: ["Avbryt", "Ta bort"],
        dangerMode: true,
      });

      if (!willDelete) return;

      const deleteSupplierApiUrl = getApiUrl(`${companyId}/purchasing/suppliers/${supplier.supplier_id}`);
      await axios.delete(deleteSupplierApiUrl, { withCredentials: true });
      swal('Borttagen!', 'Leverantören har tagits bort.', 'success');
      await fetchSuppliers();
    } catch (error) {
      swal('Error', 'Failed to delete supplier', 'error');
      console.error(error);
    }
  }, [companyId, fetchSuppliers]);

  const handleSave = async (newSupplierData) => {
    try {
      const newSupplier = {
        name: newSupplierData.name,
        orgnumber: newSupplierData.orgnumber,
        vatnumber: newSupplierData.vatnumber,
        postadress1: newSupplierData.address,
        postadress2: '',
        city: newSupplierData.city,
        postnumber: newSupplierData.postalcode ? newSupplierData.postalcode.replace(/\s/g, '') : '', // Normalize postal code
        country: newSupplierData.country,
        phonenumber: newSupplierData.phonenumber
      };

      const createSupplierApiUrl = getApiUrl(`${companyId}/purchasing/suppliers`);
      const response = await axios.post(createSupplierApiUrl, newSupplier, { withCredentials: true });
      if (response.status === 201) {
        swal('Klart!', 'Leverantören har skapats.', 'success');
        await fetchSuppliers();
      }
    } catch (error) {
      swal('Error', 'Failed to create supplier', 'error');
      console.error(error);
    }
  };

  const handleUpdate = async (updatedSupplierData) => {
    try {
      if (!selectedSupplier || !selectedSupplier.supplier_id) {
        swal('Error', 'Missing supplier ID', 'error');
        return;
      }

      const updatedSupplier = {
        supplier_id: selectedSupplier.supplier_id,
        name: updatedSupplierData.name,
        orgnumber: updatedSupplierData.orgnumber,
        vatnumber: updatedSupplierData.vatnumber,
        postadress1: updatedSupplierData.address,
        postadress2: '',
        city: updatedSupplierData.city,
        postnumber: updatedSupplierData.postalcode ? updatedSupplierData.postalcode.replace(/\s/g, '') : '', // Normalize postal code
        country: updatedSupplierData.country,
        phonenumber: updatedSupplierData.phonenumber
      };

      const updateSupplierApiUrl = getApiUrl(`${companyId}/purchasing/suppliers/${selectedSupplier.supplier_id}`);
      const response = await axios.put(updateSupplierApiUrl, updatedSupplier, { withCredentials: true });
      if (response.status === 200) {
        swal('Klart!', 'Leverantörsinformationen har uppdaterats.', 'success');
        await fetchSuppliers();
      }
    } catch (error) {
      swal('Error', 'Failed to update supplier', 'error');
      console.error(error);
    }
  };

  const PaginationControls = () => (
    <div className="d-flex justify-content-between align-items-center mt-4 pt-3" style={{ borderTop: '1px solid #e5e7eb' }}>
      <div className="d-flex align-items-center gap-3">
        <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
          Visar {page.length === 0 ? 0 : pageIndex * pageSize + 1} till {pageIndex * pageSize + page.length} av {suppliersTableData.length} leverantörer
        </span>
        <select
          className="form-select form-select-sm"
          style={{ width: 'auto', fontSize: '0.875rem' }}
          value={pageSize}
          onChange={e => {
            const size = Number(e.target.value);
            setPageSize(size);
            setTablePageSize(size);
          }}
        >
          {[10, 20, 30, 40, 50].map(size => (
            <option key={size} value={size}>{size} per sida</option>
          ))}
        </select>
      </div>

      <div className="d-flex align-items-center gap-2">
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => gotoPage(0)}
          disabled={!canPreviousPage}
          style={{ padding: '0.375rem 0.75rem' }}
        >
          Första
        </Button>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={previousPage}
          disabled={!canPreviousPage}
          style={{ padding: '0.375rem 0.75rem' }}
        >
          Föregående
        </Button>
        <span style={{ fontSize: '0.875rem', color: '#6b7280', padding: '0 1rem' }}>
          Sida {pageIndex + 1} av {pageOptions.length}
        </span>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={nextPage}
          disabled={!canNextPage}
          style={{ padding: '0.375rem 0.75rem' }}
        >
          Nästa
        </Button>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => gotoPage(pageCount - 1)}
          disabled={!canNextPage}
          style={{ padding: '0.375rem 0.75rem' }}
        >
          Sista
        </Button>
      </div>
    </div>
  );


  return (
    <>
      <style>{`
        .modern-card {
          background: white;
          border: none;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          overflow: hidden;
        }

        .modern-card:hover {
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        }


        .modern-table {
          background: white;
          border: none;
          border-radius: 12px;
          overflow: hidden;
        }

        .modern-table th {
          background: #f8fafc;
          border: none;
          padding: 1rem;
          font-weight: 600;
          font-size: 0.875rem;
          color: #1e293b;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .modern-table td {
          border: none;
          padding: 1rem;
          vertical-align: middle;
          border-bottom: 1px solid #f1f5f9;
        }

        .modern-table tbody tr {
          transition: all 0.2s ease;
          cursor: pointer;
        }

        .modern-table tbody tr:hover {
          background: #f8fafc;
          transform: translateY(-1px);
        }

        .modern-table tbody tr:last-child td {
          border-bottom: none;
        }

        @media (max-width: 768px) {
          .modern-card {
            margin-bottom: 1rem;
          }
        }
      `}</style>

      <PageTitle activeMenu="Leverantörer" motherMenu="Leverantörer och Inköp" />

      <div className="col-12">
        {/* Error State */}
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            <AlertTriangle size={16} className="me-2" />
            Ett fel uppstod vid hämtning av leverantörer. Försök igen.
          </Alert>
        )}

        {/* Loading State */}
        {loading && suppliersTableData.length === 0 && (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
            <div className="text-center">
              <Spinner animation="border" style={{ color: 'var(--bs-primary)' }} className="mb-3" />
              <div style={{ color: '#6b7280' }}>Laddar leverantörer...</div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && suppliersTableData.length === 0 && (
          <div className="modern-card">
            <div className="p-5 text-center">
              <div
                className="mx-auto mb-4 d-flex align-items-center justify-content-center"
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '20px',
                  background: 'var(--rgba-primary-1)',
                  color: 'var(--bs-primary)'
                }}
              >
                <Building size={40} />
              </div>
              <h4 style={{ fontWeight: '600', marginBottom: '1rem' }}>Inga leverantörer än</h4>
              <p style={{ color: '#6b7280', marginBottom: '2rem', maxWidth: '400px', margin: '0 auto 2rem' }}>
                Börja med att lägga till dina leverantörer för att hålla koll på utgifter och betalningar.
              </p>
              <div className="d-flex justify-content-center gap-3 flex-wrap">
                <Button
                  onClick={handleNewSupplier}
                  style={{
                    background: 'var(--bs-primary)',
                    borderColor: 'var(--bs-primary)',
                    padding: '0.75rem 1.5rem',
                    fontWeight: '600'
                  }}
                >
                  <Plus size={16} className="me-2" />
                  Skapa leverantör
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => history.push(`/${companyId}/suppliers/import`)}
                  style={{ padding: '0.75rem 1.5rem' }}
                >
                  <Upload size={16} className="me-2" />
                  Importera leverantörer
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Main Content - When we have suppliers */}
        {!loading && suppliersTableData.length > 0 && (
          <>
            {/* Main Table */}
            <div className="modern-card">
              <div className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h4 style={{ margin: 0, fontWeight: '600' }}>Leverantörer</h4>
                    <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
                      Hantera dina leverantörer och håll koll på utgifter
                    </p>
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <Button
                      variant="outline-secondary"
                      onClick={fetchSuppliers}
                      disabled={refreshing}
                      style={{ padding: '0.5rem 1rem' }}
                    >
                      <RefreshCw size={16} className={refreshing ? 'spin' : ''} />
                    </Button>

                    <Dropdown>
                      <Dropdown.Toggle
                        style={{
                          background: 'var(--bs-primary)',
                          borderColor: 'var(--bs-primary)',
                          padding: '0.5rem 1rem',
                          fontWeight: '600'
                        }}
                      >
                        <Plus size={16} className="me-2" />
                        Ny leverantör
                      </Dropdown.Toggle>
                      <Dropdown.Menu align="end">
                        <Dropdown.Item onClick={handleNewSupplier}>
                          <Plus size={16} className="me-2" />
                          Skapa ny leverantör
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item onClick={() => history.push(`/${companyId}/suppliers/import`)}>
                          <Upload size={16} className="me-2" />
                          Importera leverantörer
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="position-relative" style={{ width: '320px' }}>
                    <Search
                      size={16}
                      className="position-absolute"
                      style={{
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#6b7280'
                      }}
                    />
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Sök leverantörer..."
                      value={globalFilter}
                      onChange={(e) => setGlobalFilter(e.target.value)}
                      style={{
                        paddingLeft: '40px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        height: '40px'
                      }}
                    />
                  </div>
                </div>

                <div className="table-responsive">
                  <table {...getTableProps()} className="table modern-table w-100">
                    <thead>
                      {headerGroups.map(headerGroup => (
                        <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                          {headerGroup.headers.map(column => (
                            <th
                              {...column.getHeaderProps(column.getSortByToggleProps())}
                              key={column.id}
                              style={{ cursor: column.canSort ? 'pointer' : 'default' }}
                            >
                              <div className="d-flex align-items-center">
                                {column.render('Header')}
                                {column.canSort && (
                                  <span className="ms-2">
                                    {column.isSorted ? (column.isSortedDesc ? '↓' : '↑') : '↕'}
                                  </span>
                                )}
                              </div>
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                      {page.map(row => {
                        prepareRow(row);
                        return (
                          <tr
                            {...row.getRowProps()}
                            key={row.id}
                            onClick={() => history.push(`/${companyId}/suppliers/${row.original.supplier_id}`)}
                          >
                            {row.cells.map(cell => (
                              <td {...cell.getCellProps()} key={cell.column.id}>
                                {cell.render('Cell')}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <PaginationControls />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Supplier Modal */}
        <NewSupplierModal
          key={modalKey}
          show={showModal}
          handleClose={handleCloseModal}
          handleSave={handleSave}
          handleUpdate={handleUpdate}
          initialSupplierData={selectedSupplier}
        />
      </div>
    </>
  );
};

export default SuppliersTable;